/** Fold the retired wire counter once; the normalized result contains only lostBalls. */
export function normalizePossessionStatistics<T extends { lostBalls?: number }>(statistics: T): T {
  const { steals, ...current } = statistics as T & { steals?: number };
  if (steals == null) return current as T;
  return { ...current, lostBalls: (statistics.lostBalls ?? 0) + (steals ?? 0) } as T;
}

export type ActiveSuspension = {
  id?: string;
  matchId?: string;
  sourceEventId?: string;
  teamSide?: string;
  rosterEntryId?: string | null;
  playerId?: string | null;
  startedAtEffectiveElapsedMs?: number;
  durationMs?: number;
  expiresAtEffectiveElapsedMs?: number;
  remainingMs?: number;
  status?: string;
  updatedAt?: Date | null;
};

export type MatchOperationsState = {
  sidesSwapped?: boolean;
  matchId?: string;
  status?: string;
  period?: number;
  elapsedMs?: number;
  effectiveElapsedMs?: number;
  periodElapsedMs?: number;
  clockRunning?: boolean;
  scoreTeamA?: number;
  scoreTeamB?: number;
  teamATimeouts?: number;
  teamBTimeouts?: number;
  lastServerSequence?: number;
  lastEventAt?: Date | null;
  updatedAt?: Date | null;
  allowedCommands?: string[];
  activeSuspensions?: ActiveSuspension[];
  goalkeepers?: MatchOperationGoalkeeperState;
  participantStatistics?: MatchOperationParticipantStatistics[];
  teamStatistics?: {
    teamA: { attempts: number; goals: number; saves: number; posts: number; blockedShots: number; turnovers: number; technicalFaults: number; lostBalls: number };
    teamB: { attempts: number; goals: number; saves: number; posts: number; blockedShots: number; turnovers: number; technicalFaults: number; lostBalls: number };
  };
};

export type MatchOperationGoalkeeperTeamState = {
  designatedParticipantIds: string[];
  activeParticipantId?: string | null;
};

export type MatchOperationGoalkeeperState = {
  teamA: MatchOperationGoalkeeperTeamState;
  teamB: MatchOperationGoalkeeperTeamState;
};

export type MatchOperationParticipantStatistics = {
  participantId: string;
  teamSide: 'TEAM_A' | 'TEAM_B';
  attempts: number;
  onTarget: number;
  offTarget: number;
  goals: number;
  missedConversions: number;
  shotsFaced: number;
  saves: number;
  sevenMeterAttempts: number;
  sevenMeterGoals: number;
  sevenMeterShotsFaced: number;
  sevenMeterSaves: number;
  posts: number;
  blockedShots: number;
  blocks: number;
  turnovers: number;
  technicalFaults: number;
  lostBalls: number;
};

export type MatchOperationsTimingSettings = {
  matchId?: string;
  regularPeriodCount?: number;
  regularPeriodDurationMinutes?: number;
  overtimeEnabled?: boolean;
  overtimePeriodCount?: number;
  overtimePeriodDurationMinutes?: number;
};

export type MatchOperationsTimingSettingsUpdateRequest = {
  regularPeriodCount?: number | null;
  regularPeriodDurationMinutes?: number | null;
  overtimeEnabled?: boolean | null;
  overtimePeriodCount?: number | null;
  overtimePeriodDurationMinutes?: number | null;
};

export type MatchOperationsEvent = {
  id?: string;
  matchId?: string;
  serverSequence?: number;
  serverTimestamp?: Date;
  clientEventId?: string | null;
  type?: string;
  period?: number | null;
  elapsedMs?: number | null;
  teamId?: string | null;
  teamSide?: string | null;
  playerId?: string | null;
  targetEventId?: string | null;
  createdBy?: string | null;
  note?: string | null;
  shotOutcome?: 'GOAL' | 'SAVED' | 'MISSED' | 'POST' | 'BLOCKED' | null;
  shotType?: 'OPEN_PLAY' | 'SEVEN_METER' | null;
  goalkeeperPlayerId?: string | null;
  defenderPlayerId?: string | null;
};

export type MatchOperationsEventCreateRequest = {
  clientEventId?: string | null;
  type?: string;
  period?: number | null;
  elapsedMs?: number | null;
  teamId?: string | null;
  teamSide?: string | null;
  playerId?: string | null;
  clientTimestamp?: Date | string | null;
  note?: string | null;
};

export type MatchOperationsEventBatchRequest = {
  events?: MatchOperationsEventCreateRequest[];
  allowOutOfOrder?: boolean | null;
};

export type MatchOperationsCommandRequest = {
  command?: string;
  period?: number | null;
  elapsedMs?: number | null;
  clientEventId?: string | null;
};

export type MatchOperationsEventRejected = {
  clientEventId?: string | null;
  reason?: string;
};

export type MatchOperationsEventIngestResponse = {
  accepted?: string[];
  rejected?: MatchOperationsEventRejected[];
  events?: MatchOperationsEvent[];
  state?: MatchOperationsState;
};

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' ? value as Record<string, unknown> : {}
);

export const reviveMatchOperationsDate = (value: unknown): Date | null | undefined => {
  if (value == null) {
    return value ?? undefined;
  }
  if (value === '') {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  return new Date(value);
};

export const reviveActiveSuspension = (value: unknown): ActiveSuspension => {
  const raw = asRecord(value);
  return {
    ...raw,
    updatedAt: reviveMatchOperationsDate(raw.updatedAt),
  } as ActiveSuspension;
};

export const reviveMatchOperationsState = (value: unknown): MatchOperationsState => {
  const raw = asRecord(value);
  const suspensions = Array.isArray(raw.activeSuspensions)
    ? raw.activeSuspensions.map(reviveActiveSuspension)
    : undefined;

  return {
    ...raw,
    lastEventAt: reviveMatchOperationsDate(raw.lastEventAt),
    updatedAt: reviveMatchOperationsDate(raw.updatedAt),
    activeSuspensions: suspensions,
  } as MatchOperationsState;
};

export const reviveMatchOperationsEvent = (value: unknown): MatchOperationsEvent => {
  const raw = asRecord(value);
  return {
    ...raw,
    serverTimestamp: reviveMatchOperationsDate(raw.serverTimestamp),
  } as MatchOperationsEvent;
};

export const reviveMatchOperationsIngestResponse = (value: unknown): MatchOperationsEventIngestResponse => {
  const raw = asRecord(value);
  return {
    ...raw,
    events: Array.isArray(raw.events) ? raw.events.map(reviveMatchOperationsEvent) : undefined,
    state: raw.state ? reviveMatchOperationsState(raw.state) : undefined,
  } as MatchOperationsEventIngestResponse;
};
